import { coerceValueRef } from "../authContext";
import type {
  AuthDescriptor,
  HeaderEntryRef,
  HeaderEntryRefInput,
  ValueRef,
  ValueRefInput,
} from "../authDescriptor";

export type FetchTokenRequestInput = {
  url: ValueRefInput;
  method?: "GET" | "POST";
  headers?: HeaderEntryRefInput[];
  jsonBody?: Record<string, ValueRefInput>;
  formBody?: Record<string, ValueRefInput>;
  rawBody?: ValueRefInput;
  basicAuth?: { username: ValueRefInput; password: ValueRefInput };
};

type FetchTokenRequest = {
  url: ValueRef;
  method?: "GET" | "POST";
  headers?: HeaderEntryRef[];
  jsonBody?: Record<string, ValueRef>;
  formBody?: Record<string, ValueRef>;
  rawBody?: ValueRef;
  basicAuth?: { username: ValueRef; password: ValueRef };
};

export type FetchTokenExtract =
  | {
      from: "jsonPath";
      paths: [string, ...string[]];
      fallbackToRawBody?: boolean;
    }
  | { from: "header"; name: string }
  | { from: "cookie"; name: string };

export type FetchTokenApplyInput =
  | {
      kind: "header";
      name?: string;
      scheme?: string;
      extraHeaders?: HeaderEntryRefInput[];
    }
  | { kind: "cookie"; name: string };

type FetchTokenApply =
  | {
      kind: "header";
      name?: string;
      scheme?: string;
      extraHeaders?: HeaderEntryRef[];
    }
  | { kind: "cookie"; name: string };

function coerceHeader(h: HeaderEntryRefInput): HeaderEntryRef {
  return { name: h.name, value: coerceValueRef(h.value), scheme: h.scheme };
}

function coerceBodyRecord(
  body: Record<string, ValueRefInput> | undefined
): Record<string, ValueRef> | undefined {
  if (!body) return undefined;
  const out: Record<string, ValueRef> = {};
  for (const [k, v] of Object.entries(body)) out[k] = coerceValueRef(v);
  return out;
}

function coerceRequest(req: FetchTokenRequestInput): FetchTokenRequest {
  return {
    url: coerceValueRef(req.url),
    method: req.method,
    headers: req.headers?.map(coerceHeader),
    jsonBody: coerceBodyRecord(req.jsonBody),
    formBody: coerceBodyRecord(req.formBody),
    rawBody:
      req.rawBody !== undefined ? coerceValueRef(req.rawBody) : undefined,
    basicAuth: req.basicAuth
      ? {
          username: coerceValueRef(req.basicAuth.username),
          password: coerceValueRef(req.basicAuth.password),
        }
      : undefined,
  };
}

function coerceApply(apply: FetchTokenApplyInput): FetchTokenApply {
  if (apply.kind === "cookie") return apply;
  return {
    kind: "header",
    name: apply.name,
    scheme: apply.scheme,
    extraHeaders: apply.extraHeaders?.map(coerceHeader),
  };
}

/**
 * Emits a `fetchToken` primitive descriptor: the proxy POSTs (or GETs) a
 * token-fetch URL, extracts a token from the response, and applies it to
 * subsequent requests as a header or cookie. Use this for apps whose auth is a
 * two-step "fetch a token, then call the API" pattern, so the per-app URL,
 * request shape, and extraction path live here rather than in Rust.
 */
export function fetchTokenAuth(opts: {
  request: FetchTokenRequestInput;
  extract: FetchTokenExtract;
  apply: FetchTokenApplyInput;
}): AuthDescriptor {
  return {
    type: "primitive",
    name: "fetchToken",
    params: {
      request: coerceRequest(opts.request),
      extract: opts.extract,
      apply: coerceApply(opts.apply),
    },
  };
}
